import React, { useEffect, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Divider,
    Paper,
    Tooltip,
    Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import companyServices from 'app/services/company-services';
import userServices from 'app/services/user-services';
import { HOST } from 'app/utils/constants/paths';
import CompanyImportModal from './CompanyImportModal';
import CompanyDeactivateModal from './CompanyDeactivateModal';
import CompanyDeleteModal from './CompanyDeleteModal';
import CompanyResetDemoModal from './CompanyResetDemoModal';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';

// Ocultar la grilla es para las presentaciones: el cliente no puede ver al resto de los
// clientes. Queda guardado en este navegador para que siga oculta al recargar o al cambiar
// de empresa (activar otra recarga la pagina).
const HIDDEN_KEY = 'hermes.company_selector.hidden';

const readHidden = () => {
    try {
        return localStorage.getItem(HIDDEN_KEY) === '1';
    } catch (_e) {
        return false;
    }
};

const writeHidden = (hidden) => {
    try {
        if (hidden) localStorage.setItem(HIDDEN_KEY, '1');
        else localStorage.removeItem(HIDDEN_KEY);
    } catch (_e) {
        // Sin almacenamiento solo dura hasta recargar.
    }
};

const CompanyCard = ({ company, isSelected, isActive, onSelect, onActivate, activating, showActivateButton = true, onDeactivate = null, onReactivate = null, onDelete = null, onResetDemo = null }) => {
    return (
        <Paper
            onClick={onSelect}
            elevation={isSelected ? 4 : 1}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                cursor: 'pointer',
                minWidth: 110,
                maxWidth: 130,
                opacity: company.activated === false ? 0.5 : 1,
                border: isSelected ? '2px solid' : '2px solid transparent',
                borderColor: isSelected ? 'primary.main' : 'transparent',
                borderRadius: 2,
                position: 'relative',
                transition: 'box-shadow 0.2s, border-color 0.2s',
                '&:hover': { boxShadow: 4 },
            }}
        >
            {company.activated === false && (
                <Chip
                    label="Dada de baja"
                    size="small"
                    sx={{ position: 'absolute', top: 4, left: 4, height: 18, fontSize: 9 }}
                />
            )}
            {isActive && (
                <Tooltip title="Empresa activa">
                    <CheckCircleIcon
                        color="success"
                        sx={{ position: 'absolute', top: 6, right: 6, fontSize: 16 }}
                    />
                </Tooltip>
            )}
            {company.logo_url ? (
                <Avatar
                    src={`${HOST}${company.logo_url}`}
                    variant="rounded"
                    sx={{ width: 48, height: 48, bgcolor: 'grey.100' }}
                />
            ) : (
                <Avatar variant="rounded" sx={{ width: 48, height: 48, bgcolor: 'grey.200' }}>
                    <BusinessIcon sx={{ color: 'grey.500' }} />
                </Avatar>
            )}
            <Typography
                variant="caption"
                align="center"
                sx={{
                    fontWeight: isSelected ? 700 : 400,
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                    width: '100%',
                    textAlign: 'center',
                }}
            >
                {company.name}
            </Typography>
            {onReactivate && (
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RestoreOutlinedIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => { e.stopPropagation(); onReactivate(); }}
                    sx={{ fontSize: '0.6rem', py: 0.25, px: 0.75, mt: 0.5 }}
                >
                    Reactivar
                </Button>
            )}
            {/* Eliminar definitivamente solo aparece en una compania ya dada de baja:
                la baja es el paso reversible que da tiempo a notar el error, y este
                es el que ya no vuelve. */}
            {onDelete && (
                <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteForeverOutlinedIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    sx={{ fontSize: '0.6rem', py: 0.25, px: 0.75 }}
                >
                    Eliminar
                </Button>
            )}
            {onDeactivate && !isActive && (
                <Tooltip title="Dar de baja">
                    <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onDeactivate(); }}
                        sx={{ position: 'absolute', bottom: 2, right: 2, p: 0.25 }}
                    >
                        <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                </Tooltip>
            )}
            {showActivateButton && isSelected && !isActive && (
                <Button
                    size="small"
                    variant="outlined"
                    disabled={activating}
                    onClick={(e) => { e.stopPropagation(); onActivate(); }}
                    sx={{ fontSize: '0.65rem', py: 0.25, px: 1, mt: 0.5 }}
                >
                    {activating ? <CircularProgress size={12} /> : 'Activar'}
                </Button>
            )}
            {/* Solo en la compania demo, para el superadmin y, como Activar, al elegir su tarjeta:
                deja el guion de demostracion. */}
            {onResetDemo && isSelected && (
                <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    startIcon={<ReplayOutlinedIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => { e.stopPropagation(); onResetDemo(); }}
                    sx={{ fontSize: '0.6rem', py: 0.25, px: 0.75, lineHeight: 1.2 }}
                >
                    Restablecer demo
                </Button>
            )}
        </Paper>
    );
};

// Dos ejes independientes, y hay que poder combinarlos:
//
// - showAllCompanies: de donde sale la lista. El superadmin ve el catalogo
//   completo (puede pararse en cualquier compania); el resto solo las suyas,
//   que ya vienen en el payload de /auth.
// - mode: que hace un clic.
//     'select'   = elegir cual se edita sin salir de la propia. Solo sirve
//                  donde el backend acepta una compania ajena por parametro.
//     'activate' = cambiarse a esa compania (setCurrentCompany + reload).
//
// Integraciones es justo la combinacion que obliga a separarlos: el superadmin
// ve todas, pero ahi las escrituras del backend van contra current_company sin
// recibir parametro (ver PluginsController), asi que el clic tiene que activar.
const AddCompanyCard = ({ onClick }) => {
    return (
        <Tooltip title="Agregar compañía">
            <Paper
                onClick={onClick}
                elevation={0}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    p: 1.5,
                    cursor: 'pointer',
                    minWidth: 110,
                    maxWidth: 130,
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    color: 'text.secondary',
                    transition: 'border-color 0.2s, color 0.2s',
                    '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                }}
            >
                <Box
                    sx={{
                        width: 48,
                        height: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <AddOutlinedIcon sx={{ fontSize: 32 }} />
                </Box>
                <Typography variant="caption" align="center" sx={{ lineHeight: 1.2, width: '100%' }}>
                    Agregar compañía
                </Typography>
            </Paper>
        </Tooltip>
    );
};

const CompanySelector = ({
    selectedCompanyId,
    onCompanySelect,
    showAllCompanies = false,
    mode = 'activate',
}) => {
    const { authUser } = useJumboAuth();
    const [allCompanies, setAllCompanies] = useState([]);
    const [loading, setLoading] = useState(showAllCompanies);
    const [activating, setActivating] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [companyToDeactivate, setCompanyToDeactivate] = useState(null);
    const [companyToDelete, setCompanyToDelete] = useState(null);
    const [demoToReset, setDemoToReset] = useState(null);
    const [hidden, setHidden] = useState(readHidden);

    const companies = showAllCompanies ? allCompanies : (authUser?.companies || []);
    const isSelectMode = mode === 'select';

    const reloadCompanies = () => {
        // Con includeInactive porque el superadmin necesita ver las dadas de baja
        // para poder reactivarlas.
        companyServices.getAllCompanies(true)
            .then(setAllCompanies)
            .catch(() => {})
    };

    useEffect(() => {
        if (!showAllCompanies) return;

        companyServices.getAllCompanies(true)
            .then(setAllCompanies)
            .finally(() => setLoading(false));
    }, [showAllCompanies]);

    const reactivateCompany = (companyId) => {
        companyServices.activateCompany(companyId)
            .then(() => reloadCompanies())
            .catch(() => {})
    };

    const activateCompany = (companyId) => {
        setActivating(true);
        userServices.setCurrentCompany(companyId)
            .then(() => window.location.reload())
            .catch(() => setActivating(false));
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    // Con una sola empresa no hay nada que elegir: basta con decir cual se edita.
    if (companies.length < 2) {
        return null;
    }

    const toggleHidden = () => {
        writeHidden(!hidden);
        setHidden(!hidden);
    };

    return (
        <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: hidden ? 0 : 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0 }}>
                    {showAllCompanies ? 'Compañías' : 'Mis empresas'}
                </Typography>
                {!hidden && (
                    <Typography variant="caption" color="text.secondary">
                        ({companies.length})
                    </Typography>
                )}
                <Button
                    size="small"
                    color="inherit"
                    startIcon={hidden ? <VisibilityOutlinedIcon fontSize="small" /> : <VisibilityOffOutlinedIcon fontSize="small" />}
                    onClick={toggleHidden}
                    sx={{ ml: 'auto', color: 'text.secondary', textTransform: 'none' }}
                >
                    {hidden ? 'Mostrar compañías' : 'Ocultar compañías'}
                </Button>
            </Box>
            {hidden ? <Divider sx={{ mt: 1.5 }} /> : (
            <React.Fragment>
            {!isSelectMode && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Estás viendo la configuración de tu empresa activa. Al elegir otra te cambias a ella.
                </Typography>
            )}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    pb: 1,
                }}
            >
                {companies.map((company) => (
                    <CompanyCard
                        key={company.id}
                        company={company}
                        isSelected={company.id === selectedCompanyId}
                        isActive={company.id === authUser?.current_company?.id}
                        onSelect={() => {
                            // En una dada de baja no se opera ni se editan sus ajustes.
                            if (company.activated === false) return;
                            if (isSelectMode) return onCompanySelect(company.id);
                            // Clic en la empresa en la que ya estás: no hay nada que hacer,
                            // y recargar por gusto perdería lo que tenga sin guardar.
                            if (activating || company.id === authUser?.current_company?.id) return;
                            activateCompany(company.id);
                        }}
                        onActivate={() => activateCompany(selectedCompanyId)}
                        activating={activating}
                        showActivateButton={isSelectMode && company.activated !== false}
                        onDeactivate={showAllCompanies && company.activated !== false ? () => setCompanyToDeactivate(company) : null}
                        onReactivate={showAllCompanies && company.activated === false ? () => reactivateCompany(company.id) : null}
                        onDelete={showAllCompanies && company.activated === false ? () => setCompanyToDelete(company) : null}
                        onResetDemo={showAllCompanies && company.demo && company.activated !== false ? () => setDemoToReset(company) : null}
                    />
                ))}

                {/* Crear una compania es cosa de superadmin, los mismos que ven el
                    catalogo completo. Va como una tarjeta mas al final de la grilla,
                    con las mismas medidas para que calce con las demas. */}
                {showAllCompanies && (
                    <AddCompanyCard onClick={() => setImportOpen(true)} />
                )}
            </Box>
            <Divider sx={{ mt: 2 }} />
            </React.Fragment>
            )}

            <CompanyImportModal
                open={importOpen}
                onClose={() => setImportOpen(false)}
                onImported={reloadCompanies}
            />

            <CompanyDeactivateModal
                company={companyToDeactivate}
                open={Boolean(companyToDeactivate)}
                onClose={() => setCompanyToDeactivate(null)}
                onDeactivated={reloadCompanies}
            />

            <CompanyResetDemoModal
                company={demoToReset}
                open={Boolean(demoToReset)}
                isActive={demoToReset?.id === authUser?.current_company?.id}
                onClose={() => setDemoToReset(null)}
            />

            <CompanyDeleteModal
                company={companyToDelete}
                open={Boolean(companyToDelete)}
                onClose={() => setCompanyToDelete(null)}
                onDeleted={reloadCompanies}
            />
        </Box>
    );
};

export default CompanySelector;
